import { useState, useCallback } from 'react';
import { Input, InputNumber, Button, Spin, message, Tooltip, Typography, Space, Tag } from 'antd';
import {
  InfoCircleOutlined,
  SaveOutlined,
  CheckOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { GlobalSettingResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { WisecureCard } from '@/components/base/WisecureCard';
import { KeyValueMapEditor } from '@/components/base/KeyValueMapEditor';
import { TierListEditor } from '@/components/base/TierListEditor';
import { PlatformVersionsEditor } from '@/components/base/PlatformVersionsEditor';
import { neutral } from '@/theme';

const { Text } = Typography;

type SettingType = 'number' | 'email' | 'text' | 'json' | 'report-points' | 'density-tiers' | 'platform-versions';

interface SettingMeta {
  label: string;
  description: string;
  type: SettingType;
  suffix?: string;
  min?: number;
  max?: number;
  placeholder?: string;
}

const SETTING_META: Record<string, SettingMeta> = {
  wisecurePercentageFees: {
    label: 'Frais WiSecure (%)',
    description:
      "Pourcentage de commission prélevé par WiSecure sur chaque transaction d'intervention. Utilisé dans le calcul du prix total (sous-total + TVA + frais). Valeur par défaut : 18%.",
    type: 'number',
    suffix: '%',
    min: 0,
    placeholder: '18',
  },
  maxTimeToAcceptInterventionInMinutes: {
    label: "Temps max d'acceptation (min)",
    description:
      "Durée maximale en minutes pendant laquelle un agent peut accepter une offre d'intervention avant expiration. Converti en timestamp pour définir acceptanceExpiresAt. Valeur par défaut : 2 min.",
    type: 'number',
    suffix: 'min',
    min: 1,
    placeholder: '2',
  },
  maxTimeToPayInterventionInMinutes: {
    label: 'Temps max de paiement (min)',
    description:
      "Durée maximale en minutes pour qu'un client complète le paiement après acceptation du prix. Utilisé lors de la transition vers le statut PAYMENT_IN_PROGRESS.",
    type: 'number',
    suffix: 'min',
    min: 1,
    placeholder: '5',
  },
  maxTimeToAcceptPriceInMinutes: {
    label: 'Temps max acceptation prix (min)',
    description:
      "Durée maximale en minutes pour qu'un client accepte le prix proposé par l'agent. Génère un timestamp priceExpiresAt pour le suivi d'expiration.",
    type: 'number',
    suffix: 'min',
    min: 1,
    placeholder: '5',
  },
  maxTimeToStartMovingInMinutes: {
    label: 'Temps max début déplacement (min)',
    description:
      "Durée maximale en minutes pour qu'un agent commence son déplacement vers le lieu d'intervention après confirmation du paiement.",
    type: 'number',
    suffix: 'min',
    min: 1,
    placeholder: '10',
  },
  maxTimeToPaySurveillanceInMinutes: {
    label: 'Temps max paiement surveillance (min)',
    description:
      'Durée maximale en minutes pour compléter le paiement d\'un service de surveillance.',
    type: 'number',
    suffix: 'min',
    min: 1,
    placeholder: '5',
  },
  arrivalProximityMeters: {
    label: "Distance d'arrivée (m)",
    description:
      "Distance maximale en mètres à laquelle un agent peut se déclarer arrivé sur les lieux. La position GPS est comparée via la formule de Haversine. Si la distance dépasse cette valeur, l'agent ne peut pas changer le statut.",
    type: 'number',
    suffix: 'm',
    min: 1,
    placeholder: '200',
  },
  wizzAlerteDistanceInMeters: {
    label: 'Rayon alerte wizz (m)',
    description:
      "Rayon en mètres pour la diffusion des notifications push d'alerte wizz aux clients proches lorsqu'un rapport d'infraction est créé. Utilise une requête géospatiale.",
    type: 'number',
    suffix: 'm',
    min: 100,
    placeholder: '500',
  },
  NEIGHBORING_REPORTS_RADIUS_KM: {
    label: 'Rayon rapports voisins (km)',
    description:
      'Rayon de recherche en kilomètres pour lister les rapports de voisinage autour d\'une coordonnée GPS. Utilisé dans les requêtes PostGIS ST_DWithin.',
    type: 'number',
    suffix: 'km',
    min: 0.1,
    placeholder: '3',
  },
  sector_max_radius_security_km: {
    label: 'Rayon max secteur — sécurité (km)',
    description:
      'Rayon maximum autorisé en kilomètres pour la création ou modification d\'un secteur (zone de chalandise) par une société de sécurité. Défaut : 20 km.',
    type: 'number',
    suffix: 'km',
    min: 1,
    placeholder: '20',
  },
  sector_max_radius_installer_km: {
    label: 'Rayon max secteur — installateurs (km)',
    description:
      'Rayon maximum autorisé en kilomètres pour la création ou modification d\'un secteur (zone de chalandise) par un installateur ou distributeur. Défaut : 50 km.',
    type: 'number',
    suffix: 'km',
    min: 1,
    placeholder: '50',
  },
  neighborhood_report_points: {
    label: 'Points par type de rapport',
    description:
      'Configuration JSON mapping chaque type de rapport de voisinage à sa valeur en points pour le calcul du score de sécurité du quartier. Ex: {"BURGLARY":10,"INTRUSION_ATTEMPT":7,"SUSPECT_INDIVIDUAL":3}.',
    type: 'report-points',
    placeholder: '{"BURGLARY":10,"INTRUSION_ATTEMPT":7}',
  },
  neighborhood_offense_bonus_points: {
    label: 'Bonus points infraction',
    description:
      'Points bonus ajoutés au score du quartier lorsqu\'un rapport d\'infraction (effraction/crime) est déposé. Valeur par défaut : 15 points.',
    type: 'number',
    suffix: 'pts',
    min: 0,
    placeholder: '15',
  },
  neighborhood_alert_density_radius_km: {
    label: 'Rayon densité alerte (km)',
    description:
      'Rayon en km utilisé pour calculer la densité d\'autour d\'une position, ce qui détermine les paliers d\'expiration des alertes.',
    type: 'number',
    suffix: 'km',
    min: 0.1,
    placeholder: '3',
  },
  neighborhood_alert_expiry_tiers: {
    label: "Paliers d'expiration alertes",
    description:
      'Configuration JSON définissant les paliers de densité d\'utilisateurs et la durée d\'expiration correspondante des alertes. Plus la densité est élevée, plus l\'alerte expire vite. Ex: [{"maxUsers":50,"expiryDays":15},{"maxUsers":250,"expiryDays":1}].',
    type: 'density-tiers',
    placeholder: '[{"maxUsers":50,"expiryDays":15}]',
  },
  CLOSE_LOCATION_TO_PROTECT_IN_METERS: {
    label: 'Distance de protection (m)',
    description:
      'Distance en mètres définissant la zone de protection autour d\'un emplacement. Utilisé pour déterminer les zones sensibles à protéger.',
    type: 'number',
    suffix: 'm',
    min: 1,
    placeholder: '100',
  },
  MAX_ACCEPTED_REJECTED_INTERVENTIONS: {
    label: 'Taux max de rejet (%)',
    description:
      'Pourcentage maximum d\'interventions refusées ou rejetées autorisé pour un agent au-delà duquel des mesures sont prises.',
    type: 'number',
    suffix: '%',
    min: 0,
    max: 100,
    placeholder: '30',
  },
  MAX_DISTANCE_BETWEEN_SECTORS_IN_METERS: {
    label: 'Distance max entre secteurs (m)',
    description:
      'Distance maximale en mètres autorisée entre deux secteurs pour la planification et la couverture géographique.',
    type: 'number',
    suffix: 'm',
    min: 1,
    placeholder: '5000',
  },
  WISECURE_SUPPORT_EMAIL: {
    label: 'Email de support',
    description:
      'Adresse email du support WiSecure. Utilisée pour les communications de support technique et les notifications système.',
    type: 'email',
    placeholder: 'support@wisecure.com',
  },
  INSTALLER_REQUEST_COPY_EMAIL: {
    label: "Email copie demande d'installation",
    description:
      'Adresse email en copie pour les demandes d\'installation d\'agent de sécurité. Reçoit une copie de chaque demande soumise.',
    type: 'email',
    placeholder: 'install@wisecure.com',
  },
  DISTANCE_TO_REACH_TARGET: {
    label: 'Distance pour atteindre la cible (m)',
    description:
      'Distance en mètres qu\'un agent doit parcourir pour atteindre la cible d\'une intervention. Utilisé pour le suivi de progression.',
    type: 'number',
    suffix: 'm',
    min: 1,
    placeholder: '500',
  },
  SOCIETY_OWNER_DOCUMENT_VALIDITY: {
    label: 'Validité documents société (mois)',
    description:
      'Durée de validité en mois des documents fournis par le propriétaire d\'une société de sécurité. Au-delà, un renouvellement est requis.',
    type: 'number',
    suffix: 'mois',
    min: 1,
    placeholder: '12',
  },
  breakingChangesVersion: {
    label: 'Versions breaking changes',
    description:
      'Versions minimales requises par plateforme (Client/Pro) et OS (Android/iOS) pour lesquelles un changement incompatible a été introduit. Les apps en dessous de ces versions seront bloquées.',
    type: 'platform-versions',
  },
};

export function SettingsPage() {
  const invalidate = useInvalidateQueries();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());

  const { data: settings, isLoading } = useWisecureQuery<GlobalSettingResponse[]>(
    queryKeys.globalSettings,
    (client) => client.globalSettings.globalSettingsControllerFindAll(),
  );

  const settingsMap: Record<string, string> = {};
  if (settings) {
    for (const s of settings) {
      settingsMap[s.key] = s.value;
    }
  }

  const updateMutation = useWisecureMutation(
    (client, { key, value }: { key: string; value: string }) =>
      client.globalSettings.globalSettingsControllerUpdate({
        key,
        requestBody: { value },
      }),
    {
      onSuccess: (_data, variables) => {
        message.success(`"${variables.key}" mis à jour`);
        setEdits((prev) => {
          const next = { ...prev };
          delete next[variables.key];
          return next;
        });
        setSavedKeys((prev) => new Set(prev).add(variables.key));
        invalidate(queryKeys.globalSettings);
        setTimeout(() => {
          setSavedKeys((prev) => {
            const next = new Set(prev);
            next.delete(variables.key);
            return next;
          });
        }, 2000);
      },
      onError: (_err, variables) => {
        message.error(`Erreur lors de la mise à jour de "${variables.key}"`);
      },
    },
  );

  const handleChange = useCallback((key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setSavedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleSave = useCallback(
    (key: string) => {
      const value = edits[key];
      if (value === undefined) return;
      updateMutation.mutate({ key, value });
    },
    [edits, updateMutation],
  );

  const currentValue = (key: string) => edits[key] ?? settingsMap[key] ?? '';
  const isDirty = (key: string) => key in edits && edits[key] !== (settingsMap[key] ?? '');
  const isSaved = (key: string) => savedKeys.has(key);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const allKeys = [
    ...Object.keys(SETTING_META),
    ...Object.keys(settingsMap).filter((k) => !(k in SETTING_META)),
  ];

  const REPORT_POINTS_LABELS: Record<string, string> = {
    SUSPECT_INDIVIDUAL: 'Individu suspect',
    SUSPICIOUS_VEHICLE: 'Véhicule suspect',
    INTRUSION_ATTEMPT: "Tentative d'intrusion",
    BURGLARY: 'Effraction',
    SPYING_AND_TRACKING: 'Espionnage et filature',
    OTHER: 'Autre',
  };

  const renderInput = (key: string, meta: SettingMeta | undefined) => {
    const m: SettingMeta = meta ?? { label: key, description: '', type: 'text' };
    const val = currentValue(key);

    if (m.type === 'report-points') {
      return (
        <KeyValueMapEditor
          value={val}
          onChange={(json) => handleChange(key, json)}
          knownKeys={REPORT_POINTS_LABELS}
        />
      );
    }
    if (m.type === 'density-tiers') {
      return (
        <TierListEditor
          value={val}
          onChange={(json) => handleChange(key, json)}
        />
      );
    }
    if (m.type === 'platform-versions') {
      return (
        <PlatformVersionsEditor
          value={val}
          onChange={(json) => handleChange(key, json)}
        />
      );
    }

    if (m.type === 'number') {
      const input = (
        <InputNumber
          value={val}
          onChange={(v) => handleChange(key, String(v ?? ''))}
          min={m.min != null ? String(m.min) : undefined}
          max={m.max != null ? String(m.max) : undefined}
          placeholder={m.placeholder}
          controls={false}
          stringMode
          style={{ flex: 1, minWidth: 160 }}
        />
      );

      if (m.suffix) {
        return (
          <Space.Compact style={{ flex: 1, minWidth: 160 }}>
            {input}
            <Input value={m.suffix} disabled style={{ width: 50, textAlign: 'center', pointerEvents: 'none' }} />
          </Space.Compact>
        );
      }

      return input;
    }
    if (m.type === 'email') {
      return (
        <Input
          value={val}
          onChange={(e) => handleChange(key, e.target.value)}
          type="email"
          placeholder={m.placeholder}
          autoComplete="email"
          style={{ flex: 1 }}
        />
      );
    }
    if (m.type === 'json') {
      return (
        <Input.TextArea
          value={val}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder={m.placeholder}
          autoSize={{ minRows: 2, maxRows: 6 }}
          style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}
        />
      );
    }
    return (
      <Input
        value={val}
        onChange={(e) => handleChange(key, e.target.value)}
        placeholder={m.placeholder}
        style={{ flex: 1 }}
      />
    );
  };

  return (
    <div>
      <PageHeader
        title="Réglages de l'application"
        subtitle="Paramètres globaux du système — chaque champ se met à jour individuellement"
        extra={
          <Tag icon={<SettingOutlined />} color="blue">
            {allKeys.length} paramètres
          </Tag>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {allKeys.map((key) => {
          const meta = SETTING_META[key];
          const dirty = isDirty(key);
          const saved = isSaved(key);

          return (
            <WisecureCard
              key={key}
              style={{
                padding: 0,
                border: dirty
                  ? '1px solid #d48806'
                  : saved
                    ? '1px solid #389e0d'
                    : `1px solid ${neutral[100]}`,
                transition: 'border-color 0.3s',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: meta?.type === 'json' || meta?.type === 'report-points' || meta?.type === 'density-tiers' || meta?.type === 'platform-versions' ? 'flex-start' : 'center',
                  gap: 16,
                  padding: '16px 20px',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 280,
                    flexShrink: 0,
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text strong style={{ fontSize: 14 }}>
                        {meta?.label ?? key}
                      </Text>
                      {meta?.description && (
                        <Tooltip
                          title={meta.description}
                          placement="topLeft"
                          styles={{ root: { maxWidth: 360 } }}
                        >
                          <InfoCircleOutlined
                            style={{
                              color: neutral[300],
                              cursor: 'help',
                              fontSize: 14,
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, fontFamily: 'monospace' }}
                    >
                      {key}
                    </Text>
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  {renderInput(key, meta)}
                </div>

                <Space size={8}>
                  {saved && (
                    <Button
                      type="text"
                      icon={<CheckOutlined />}
                      style={{ color: '#389e0d' }}
                      disabled
                    >
                      Sauvé
                    </Button>
                  )}
                  <Button
                    type={dirty ? 'primary' : 'default'}
                    icon={<SaveOutlined />}
                    onClick={() => handleSave(key)}
                    disabled={!dirty}
                    loading={updateMutation.isPending}
                  >
                    Mettre à jour
                  </Button>
                </Space>
              </div>
            </WisecureCard>
          );
        })}
      </div>
    </div>
  );
}
